import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import GqlClientContext from 'contexts/GqlClientContext';
import search from 'hooks/grids/search';
import useDateScope from 'hooks/useDateScope';
import captureException from 'services/captureException';

/**
 * Fetches the number of records each candidate filter would return, via one
 * small search2 request per distinct filter set.
 *
 * For each candidate:
 * - ownCount: baseFilters + the candidate
 * - count: baseFilters + selectedFilters + the candidate (for a selected
 * candidate, its filterInput is already one of the selectedFilters)
 *
 * count only differs from ownCount when other filters are selected, so it is
 * only fetched then.
 */
const useQuickFilterCounts = ({
  dataType,
  baseFilters,
  selectedFilters,
  candidates,
  isEnabled = true,
}: {
  dataType: string;
  baseFilters: FilterInput[];
  selectedFilters: FilterInput[];
  candidates: QuickFilterCounts.Candidate[];
  isEnabled?: boolean;
}): QuickFilterCounts.CountsById => {
  const { client } = useContext(GqlClientContext);
  const dateScope = useDateScope({});
  const [ownCounts, setOwnCounts] = useState<Record<string, number>>({});
  const [combinedCounts, setCombinedCounts] = useState<Record<string, number>>(
    {},
  );
  const [isOwnLoading, setIsOwnLoading] = useState<boolean>(isEnabled);
  const [isCombinedLoading, setIsCombinedLoading] = useState<boolean>(false);

  const hasCombinedCounts = selectedFilters.length > 0;
  // count matches ownCount when nothing is selected, or for a lone selected
  // filter (its combined filter set is its own one)
  const isOwnFilterSet = useCallback(
    ({ candidate }: { candidate: QuickFilterCounts.Candidate }) =>
      !hasCombinedCounts ||
      (candidate.isSelected && selectedFilters.length === 1),
    [hasCombinedCounts, selectedFilters.length],
  );

  // The callers' filter arrays are rebuilt on every selection change, and
  // search2 responses aren't cached, so the effects key off the serialised
  // request rather than array identity: own counts are only refetched when
  // their filters actually change.
  const ownCountsKey = useMemo(
    () =>
      JSON.stringify({
        dataType,
        dateScope,
        requests: candidates.map((candidate) => ({
          id: candidate.id,
          filters: [...baseFilters, candidate.filterInput],
        })),
      }),
    [baseFilters, candidates, dataType, dateScope],
  );

  const combinedCountsKey = useMemo(
    () =>
      JSON.stringify({
        dataType,
        dateScope,
        requests: hasCombinedCounts
          ? candidates
              .filter((candidate) => !isOwnFilterSet({ candidate }))
              .map((candidate) => ({
                id: candidate.id,
                filters: candidate.isSelected
                  ? [...baseFilters, ...selectedFilters]
                  : [...baseFilters, ...selectedFilters, candidate.filterInput],
              }))
          : [],
      }),
    [
      baseFilters,
      candidates,
      dataType,
      dateScope,
      hasCombinedCounts,
      isOwnFilterSet,
      selectedFilters,
    ],
  );

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let isActive = true;
    setIsOwnLoading(true);
    fetchCounts({ client, ...JSON.parse(ownCountsKey) }).then((counts) => {
      if (isActive) {
        setOwnCounts(counts);
        setIsOwnLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [client, isEnabled, ownCountsKey]);

  useEffect(() => {
    if (!isEnabled || !hasCombinedCounts) {
      return;
    }

    let isActive = true;
    setIsCombinedLoading(true);
    fetchCounts({ client, ...JSON.parse(combinedCountsKey) }).then((counts) => {
      if (isActive) {
        setCombinedCounts(counts);
        setIsCombinedLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [client, combinedCountsKey, hasCombinedCounts, isEnabled]);

  return useMemo(() => {
    const result: QuickFilterCounts.CountsById = {};
    if (!isEnabled) {
      return result;
    }

    candidates.forEach((candidate) => {
      const ownCount = ownCounts[candidate.id];
      const isOwnSet = isOwnFilterSet({ candidate });
      result[candidate.id] = {
        count: isOwnSet ? ownCount : combinedCounts[candidate.id],
        ownCount,
        // "x of y" needs both counts
        isLoading: isOwnLoading || (!isOwnSet && isCombinedLoading),
      };
    });

    return result;
  }, [
    candidates,
    combinedCounts,
    isCombinedLoading,
    isEnabled,
    isOwnFilterSet,
    isOwnLoading,
    ownCounts,
  ]);
};

const fetchCounts = async ({
  requests,
  dataType,
  dateScope,
  client,
}: {
  requests: { id: string; filters: FilterInput[] }[];
  dataType: string;
  dateScope: DateRangeInput;
  client: ApolloClient<NormalizedCacheObject>;
}): Promise<Record<string, number>> => {
  // Several candidates can share a filter set (e.g. every selected filter
  // when more than one is selected), so request each set once
  const countByFilters = new Map<string, Promise<number | undefined>>();
  const getCount = (filters: FilterInput[]) => {
    const key = JSON.stringify(filters);
    const existing = countByFilters.get(key);
    if (existing) {
      return existing;
    }

    const request = search({
      dataType,
      filters,
      dateScope,
      sortBy: [],
      client,
      size: 1,
    })
      .then((result) => result.count)
      .catch((ex) => {
        captureException(ex);
        return undefined;
      });
    countByFilters.set(key, request);
    return request;
  };

  const results = await Promise.all(
    requests.map(async ({ id, filters }) => ({
      id,
      count: await getCount(filters),
    })),
  );

  const counts: Record<string, number> = {};
  results.forEach(({ id, count }) => {
    if (count !== undefined) {
      counts[id] = count;
    }
  });
  return counts;
};

export default useQuickFilterCounts;
